import { GetServerSideProps } from 'next';
import { getApolloClient } from '@/lib/ApolloClient';
import getDeviceCharacteristics from '@/utils/getDeviceCharacteristics';
import reqHeaderToEntriesObj from '@/utils/reqHeaderToEntriesObj';
import { SERVER_FEATURE_FLAGS, ServiceIdForMember, UI_FEATURE_FLAGS } from '@/constants';
import { costGuide as costGuideQuery } from '@/API/costGuide';
import { costGuideComparisons as costGuideComparisonsQuery } from '@/API/costGuideComparisons';
import { getSegmentationLinkSections as costGuideLinkSectionsQuery } from '@/API/getSegmentationLinkSections';
import { costGuideServiceSpecialtiesQuery } from '@/API/costGuideServiceSpecialties';
import { FeatureFlags, GAMTargetingRules, PageTemplate } from '@/types/common';
import { getGAMContentType } from '@/utils/GoogleAdsManager/getGAMContentType';
import { costGuideVerticalOptions } from './CostGuideSlimSearch';
import { getGAMVerticalShortName } from '../../../utils/GoogleAdsManager/getGAMVerticalShortName';

export const getServerSideProps: GetServerSideProps = async ({ query, req, res }) => {
  const startPageGSSP = performance.now();
  const apolloOptions = {
    logError: req.log.error.bind(req.log),
    logInfo: req.log.info.bind(req.log),
  };

  const startApolloClient = performance.now();
  const apolloClient = getApolloClient(apolloOptions);
  const stopApolloClient = performance.now();
  const serverFeatureFlags = req.careContext?.ldServerFlags ?? false;
  const showCostGuideLocalPage =
    serverFeatureFlags &&
    serverFeatureFlags[SERVER_FEATURE_FLAGS.COST_GUIDE_LOCAL_PAGE]?.variationIndex === 1;
  const isLocal = process.env.NODE_ENV === 'development';
  const userAgent = req.headers['user-agent'] || '';
  const botInfo = req.headers['Akamai-Bot']?.toString() || '';
  const deviceCharHeader = req.headers['x-akamai-device-characteristics']?.toString() || '';
  const deviceChar = isLocal
    ? getDeviceCharacteristics(userAgent, deviceCharHeader)
    : reqHeaderToEntriesObj(deviceCharHeader, ';');

  const { segment, location } = query;
  const seoSegmentId = `/cost/${segment}/${location}`;

  const { ldClient, ldUser } = req.careContext;
  const ldClientFlags: FeatureFlags = {};

  if (ldClient && ldUser) {
    ldUser.custom = {
      ...ldUser.custom,
      seoSegmentId,
      pageTemplate: PageTemplate.COST_GUIDE,
    };

    const clientFlagPromises = Object.values(UI_FEATURE_FLAGS).map(async (flag) => {
      ldClientFlags[flag.id] = await ldClient.variationDetail(flag.id, ldUser, undefined);
    });
    await Promise.all(clientFlagPromises);
  }

  const startGetPageData = performance.now();
  const [costGuideResponse, costGuideComparisonsResponse, costGuideLinkSectionsResponse] =
    await Promise.all([
      costGuideQuery(apolloClient, req.log, { id: seoSegmentId }),
      costGuideComparisonsQuery(apolloClient, req.log, { costGuideComparisonsId: seoSegmentId }),
      costGuideLinkSectionsQuery(apolloClient, {
        seoSegmentId,
      }),
    ]);
  const stopGetPageData = performance.now();

  if (costGuideResponse.error || costGuideComparisonsResponse.error) {
    const errors = {
      costGuideErrors: costGuideResponse.error,
      costGuideComparisonsErrors: costGuideComparisonsResponse.error,
    };

    req.log.error({
      event: 'costGuidePageLoadError',
      seoSegmentId,
      userAgent,
      deviceChar,
      query,
      res,
      errors,
    });

    res.statusCode = 404;

    return {
      props: {
        errors,
        seoSegmentId,
        deviceChar,
        ldClientFlags,
        additionalGAMRules: {
          vertical: 'none',
          contType: 'none',
        },
      },
    };
  }

  if (!showCostGuideLocalPage) {
    return {
      notFound: true,
    };
  }

  const { costGuide } = costGuideResponse;
  const { costGuideComparisons } = costGuideComparisonsResponse;
  const { queryResponse } = costGuideLinkSectionsResponse;

  const coreVertical = costGuide?.searchDefaults?.service?.serviceId
    ? Object.keys(costGuideVerticalOptions).find(
        (key) => key === costGuide.searchDefaults.service.serviceId
      )
    : '';

  const getServiceSpecialties = async () => {
    if (coreVertical) {
      const { serviceSpecialties = [] } = await costGuideServiceSpecialtiesQuery(
        apolloClient,
        req.log,
        {
          serviceId: coreVertical
            ? ServiceIdForMember[coreVertical as keyof typeof ServiceIdForMember]
            : '',
        }
      );

      return serviceSpecialties;
    }

    return [];
  };

  const serviceSpecialties = await getServiceSpecialties();
  const costGuideLinkSections = queryResponse?.data?.getSEOSegmentationLinkSections;
  const additionalGAMRules: Partial<GAMTargetingRules> = {
    vertical: getGAMVerticalShortName(costGuide?.service.czenServiceId ?? 'none'),
    contType: getGAMContentType({ pageTemplate: PageTemplate.COST_GUIDE }),
  };

  const pageData = {
    costGuide,
    costGuideComparisons,
    costGuideLinkSections,
    costGuideServiceSpecialties: serviceSpecialties,
  };

  const pageProps = {
    props: {
      pageData,
      seoSegmentId,
      deviceChar,
      ldClientFlags,
      additionalGAMRules,
    },
  };

  req.log.info({
    event: 'costGuidePageLoadSuccess',
    seoSegmentId,
    userAgent,
    deviceChar,
    query,
    res,
    botInfo,
  });

  const stopPageGSSP = performance.now();
  req.careContext.serverTimings.pageGSSP = stopPageGSSP - startPageGSSP;
  req.careContext.serverTimings.apolloClient = stopApolloClient - startApolloClient;
  req.careContext.serverTimings.pageData = stopGetPageData - startGetPageData;

  return pageProps;
};
